<template>
  <v-skeleton-loader :loading="loading" type="table">
    <v-data-table
      :headers="headers"
      :header-props="{ sortByText: $t('sortBy') }"
      :items="items"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-for="item in items" :key="item.name">
            <td>{{ item.name }}</td>
            <td>{{ item.minimumSelections }}</td>
            <td>{{ item.maximumSelections }}</td>
            <td>
              <v-icon small class="mr-2" @click="editItem(item)"
                >mdi-pencil</v-icon
              >
              <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
            </td>
          </tr>
        </tbody>
      </template>

      <template v-slot:top>
        <v-toolbar flat color="white">
          <div class="flex-grow-1"></div>
          <v-dialog v-model="dialog" max-width="800px" persistent>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" text rounded class="mb-2" v-on="on">{{
                $t("newOption")
              }}</v-btn>
            </template>
            <v-card>
              <v-card-title class="headline">{{ formTitle }}</v-card-title>
              <v-form ref="form" v-model="valid">
                <v-card-text class="pt-0">
                  <v-container>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="editedItem.name"
                          :rules="[rules.required]"
                          :label="`${$t('name')} *`"
                          :placeholder="$t('sideDish')"
                          rounded
                          filled
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <v-text-field
                          v-model="editedItem.minimumSelections"
                          :rules="[rules.required, rules.minimalValue]"
                          type="number"
                          :label="`${$t('minimumSelections')} *`"
                          rounded
                          filled
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <v-text-field
                          v-model="editedItem.maximumSelections"
                          :rules="[rules.required, rules.minimalValue]"
                          type="number"
                          :label="`${$t('maximumSelections')} *`"
                          rounded
                          filled
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2" sm="1">
                        <v-btn icon color="#0360ec" @click="addOptionValue()">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col cols="10" sm="11">
                        <v-expansion-panels accordion flat>
                          <v-expansion-panel
                            v-for="(value, i) in editedItem.values"
                            :key="i"
                          >
                            <v-expansion-panel-header>
                              <template v-slot:actions>
                                <v-btn
                                  icon
                                  small
                                  color="error"
                                  class="mr-2"
                                  @click.native.stop="removeOptionValue(value)"
                                >
                                  <v-icon color="error">mdi-minus</v-icon>
                                </v-btn>
                                <v-icon>$expand</v-icon>
                              </template>
                              <v-row no-gutters v-if="$vuetify.breakpoint.xs">
                                <v-col cols="12">{{ value.name }}</v-col>
                              </v-row>
                              <v-row no-gutters v-else>
                                <v-col cols="6">{{ value.name }}</v-col>
                                <v-col cols="3" class="text--secondary">
                                  {{ $t("price") }}: {{ value.price }}
                                  {{ currencySymbol }}
                                </v-col>
                                <v-col cols="3" class="text--secondary">
                                  {{ $t("maximum") }}: {{ value.maximumAmount }}
                                </v-col>
                              </v-row>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <v-row>
                                <v-col cols="12" sm="6" md="2">
                                  <v-text-field
                                    v-model="value.id"
                                    :label="$t('id')"
                                    rounded
                                    filled
                                    hide-details
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="5">
                                  <v-text-field
                                    v-model="value.name"
                                    :rules="[rules.required]"
                                    :label="`${$t('name')} *`"
                                    :placeholder="$t('sweetPotatoFries')"
                                    rounded
                                    filled
                                    hide-details
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="2">
                                  <v-text-field
                                    v-model="value.price"
                                    :rules="[
                                      rules.required,
                                      rules.minimalValue,
                                    ]"
                                    type="number"
                                    :label="`${$t('price')} *`"
                                    rounded
                                    filled
                                    hide-details
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                  <v-text-field
                                    v-model="value.maximumAmount"
                                    :rules="[
                                      rules.required,
                                      rules.minimalValue,
                                    ]"
                                    type="number"
                                    :label="`${$t('maximumAmount')} *`"
                                    rounded
                                    filled
                                    hide-details
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <div class="flex-grow-1"></div>
                  <v-btn color="primary" text rounded @click="close">{{
                    $t("close")
                  }}</v-btn>
                  <v-btn
                    color="success"
                    text
                    rounded
                    @click="save"
                    :disabled="!valid"
                    >{{ $t("save") }}</v-btn
                  >
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:no-data>
        {{ $t("noData") }}
      </template>
    </v-data-table>
  </v-skeleton-loader>
</template>

<script>
import restaurantService from "../services/restaurant.service";

export default {
  data() {
    return {
      loading: true,
      valid: true,
      rules: {
        required: (value) =>
          (value !== "" && value !== null) || this.$t("requiredField"),
        minimalValue: (value) =>
          Number(value) >= 0 || this.$t("minimalAmountIs", { amount: 0 }),
      },
      dialog: false,
      headers: [
        { text: this.$t("name"), value: "name" },
        {
          text: this.$t("minimumSelections"),
          value: "minimumSelections",
          sortable: false,
        },
        {
          text: this.$t("maximumSelections"),
          value: "maximumSelections",
          sortable: false,
        },
        { text: this.$t("actions"), value: "action", sortable: false },
      ],
      items: [],
      editedIndex: -1,
      editedItem: {
        name: "",
        minimumSelections: 0,
        maximumSelections: 1,
        values: [
          {
            id: null,
            name: this.$t("sweetPotatoFries"),
            price: 2,
            maximumAmount: 1,
          },
        ],
      },
      defaultItem: {
        name: "",
        minimumSelections: 0,
        maximumSelections: 1,
        values: [
          {
            id: null,
            name: this.$t("sweetPotatoFries"),
            price: 2,
            maximumAmount: 1,
          },
        ],
      },
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("addNewOption")
        : this.$t("editOption");
    },
    currencySymbol() {
      return localStorage.currencySymbol;
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      const resp = await restaurantService.getRestaurant(
        localStorage.getItem("restaurantId"),
        "options"
      );
      this.items = resp;
      this.loading = false;
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    addOptionValue() {
      this.editedItem.values.push({
        name: this.$t("sweetPotatoFries"),
        price: 2,
        maximumAmount: 1,
      });
    },
    removeOptionValue(value) {
      this.editedItem.values = this.editedItem.values.filter((e) => e != value);
    },
    async deleteItem(item) {
      if (window.confirm(this.$t("areYouSureYouWantToDeleteThisOption"))) {
        const resp = await restaurantService.removeOption(
          localStorage.getItem("restaurantId"),
          item._id
        );
        this.items = resp;
      }
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },
    async save() {
      if (this.editedIndex > -1) {
        const resp = await restaurantService.editOption(
          localStorage.getItem("restaurantId"),
          this.editedItem
        );
        this.items = resp;
      } else {
        const resp = await restaurantService.addOption(
          localStorage.getItem("restaurantId"),
          this.editedItem
        );
        this.items = resp;
      }
      this.close();
    },
  },
};
</script>

<style scoped>
.active-category-class {
  font-weight: bold;
}

.v-data-table .v-toolbar__content {
  float: left;
  padding: 0;
}

.v-btn--round .v-btn__content .v-icon {
  color: unset !important;
}
</style>